import {
  GsFormField,
  GsFormFieldAlignment,
  GsFormFieldAlignmentHorizontal,
  GsFormFieldAlignmentVertical,
  GsFormFieldSelect,
  GsFormIf
} from '../../../components';
import { GsStructHorizontalAlignment, GsStructTextAlignment, GsStructVerticalAlignment } from '../../../models';

export const HORIZONTAL_ALIGNMENT_SCHEMA: GsFormField<never>[] = [
  {
    formControlName: 'horizontalAlignment',
    label: 'schema.alignment.horizontal',
    type: 'alignment',
    direction: 'horizontal',
    default: GsStructHorizontalAlignment.center
  } as GsFormFieldAlignment
];

export const VERTICAL_ALIGNMENT_SCHEMA: GsFormField<never>[] = [
  {
    formControlName: 'verticalAlignment',
    label: 'schema.alignment.vertical',
    type: 'alignment',
    direction: 'vertical',
    default: GsStructVerticalAlignment.top
  } as GsFormFieldAlignment
];

/**
 * Creates an alignment schema field
 * @param direction 'horizontal' | 'vertical' - the direction of the alignment
 * @param suffix will be added at the end of the formControlName - 'horizontalAlignment' | 'verticalAlignment'
 * @param defaultValue the default value of the alignment
 * @param groupingName if set, the field will be grouped under this name
 * @param ifArgs condition to show the field
 * @param translationRoot the translation root for the label
 * @returns GsFormFieldAlignment
 */
export function alignmentSchema(
  direction: 'horizontal' | 'vertical',
  suffix = '',
  defaultValue?: GsStructHorizontalAlignment | GsStructVerticalAlignment,
  groupingName?: string,
  ifArgs?: GsFormIf,
  translationRoot?: string
): GsFormFieldAlignment {
  if (!translationRoot) {
    translationRoot = direction === 'horizontal' ? 'schema.alignment.horizontal' : 'schema.alignment.vertical';
  }
  if (direction === 'vertical') {
    return {
      formControlName: 'verticalAlignment' + suffix,
      label: translationRoot,
      type: 'alignment',
      direction: direction,
      default: defaultValue ?? GsStructVerticalAlignment.top,
      groupingName,
      if: ifArgs
    } as GsFormFieldAlignmentVertical;
  } else {
    return {
      formControlName: 'horizontalAlignment' + suffix,
      label: translationRoot,
      type: 'alignment',
      direction: direction,
      default: defaultValue ?? GsStructHorizontalAlignment.center,
      groupingName,
      if: ifArgs
    } as GsFormFieldAlignmentHorizontal;
  }
}

export const TEXT_ALIGNMENT_SCHEMA: GsFormField<never>[] = [
  {
    formControlName: 'itemTextAlignment',
    label: 'schema.alignment.text.name',
    type: 'select',
    valueType: 'number',
    default: GsStructTextAlignment.center,
    options: [
      { text: 'schema.alignment.text.left', value: GsStructTextAlignment.left },
      { text: 'schema.alignment.text.center', value: GsStructTextAlignment.center },
      { text: 'schema.alignment.text.right', value: GsStructTextAlignment.right },
      { text: 'schema.alignment.text.justify', value: GsStructTextAlignment.justify }
    ]
  } as GsFormFieldSelect<GsStructTextAlignment>
];

export function textAlignmentSchemaFields(
  suffix = '',
  defaultValue?: GsStructTextAlignment.center,
  groupingName?: string,
  ifArgs?: GsFormIf,
  translationRoot: string = 'schema.alignment'
): GsFormField<never>[] {
  return [
    {
      formControlName: 'itemTextAlignment' + suffix,
      label: translationRoot + '.text',
      type: 'select',
      valueType: 'number',
      if: ifArgs,
      groupingName: groupingName ?? 'schema.alignment.text.name' + suffix,
      default: defaultValue ?? GsStructTextAlignment.center,
      options: [
        { text: 'schema.alignment.text.left', value: GsStructTextAlignment.left },
        { text: 'schema.alignment.text.center', value: GsStructTextAlignment.center },
        { text: 'schema.alignment.text.right', value: GsStructTextAlignment.right },
        { text: 'schema.alignment.text.justify', value: GsStructTextAlignment.justify }
      ]
    }
  ];
}
